import _ from 'lodash';

import { HUBBLE_URI } from 'omega-rtc/config';
import {
  AudienceConfigApi,
  AudienceSourceType,
  ContentConfigStatus,
} from 'omega-rtc/types/ts/content-config';

// A campaign whose segmentType is CAMPAIGN_SEGMENT_TYPE_AUDIENCE stores the
// Audience's stable key (a UUID) in targetSegments, not a SegP segment value.
export const CAMPAIGN_SEGMENT_TYPE_AUDIENCE = 'audience';

export const DEFAULT_SEGMENT_NAMESPACE = 'shared';

// Hubble addresses a dataset by DataHub URN. An Audience table source is
// `<schema>.<table>`; Hubble qualifies the same table with the data-lake
// catalog and the PROD fabric. The URN is not URL-encoded because the working
// Hubble link carries its ':' and '()' raw — which is only safe while both
// identifiers are known to be free of URL metacharacters, so this module
// enforces that itself rather than trusting create-time validation.
const HUBBLE_DATASET_PLATFORM = 'presto-on-hive';
const HUBBLE_DATASET_CATALOG = 'gt_aws_hive';
const HUBBLE_DATASET_FABRIC = 'PROD';

// Mirrors audienceTableSourcePattern, which the Audience form applies to each
// half of a table source at create time.
const AUDIENCE_TABLE_IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/;

// getAudienceTableUrl returns '' for anything that is not exactly
// `<schema>.<table>` with both halves valid identifiers, so a malformed source
// renders no link at all rather than a URN pointing at the wrong dataset.
export const getAudienceTableUrl = (source: string): string => {
  const parts = _.toString(source).trim().split('.');
  if (
    parts.length !== 2
    || !AUDIENCE_TABLE_IDENTIFIER.test(parts[0])
    || !AUDIENCE_TABLE_IDENTIFIER.test(parts[1])
  ) {
    return '';
  }
  const dataset = `${HUBBLE_DATASET_CATALOG}.${parts[0]}.${parts[1]}`;
  const urn = `urn:li:dataset:(urn:li:dataPlatform:${HUBBLE_DATASET_PLATFORM},${dataset},${HUBBLE_DATASET_FABRIC})`;
  return `${HUBBLE_URI}/dataset/${urn}`;
};

export interface SegmentSourceParts {
  namespace: string;
  segmentName: string;
}

// splitSegmentSource splits a SegP combined value `<namespace>.<segmentName>`
// on its first '.', so a multi-dot name keeps its remainder. A bare name falls
// back to the `shared` namespace. It normalises non-breaking spaces exactly as
// getSegmentUrl does, so a preview and its CDP link target the same segment.
export const splitSegmentSource = (source: string): SegmentSourceParts => {
  const value = _.toString(source).replace(/\u00A0/g, ' ').trim();
  const separatorIndex = value.indexOf('.');
  const hasNamespace = separatorIndex > 0;

  return {
    namespace: hasNamespace
      ? value.slice(0, separatorIndex)
      : DEFAULT_SEGMENT_NAMESPACE,
    segmentName: hasNamespace ? value.slice(separatorIndex + 1) : value,
  };
};

export interface AudienceSegmentDisplay {
  audienceName: string;
  isSegment: boolean;
  namespace: string;
  segmentName: string;
  source: string;
}

// pickAudienceConfig chooses which of the rows GetAudience returned describes
// the audience as it is live today. UCD returns at most one ACTIVE plus one
// PENDING_APPROVAL row, so prefer ACTIVE and fall back to whatever single row
// exists (a never-approved audience has only a pending one).
export const pickAudienceConfig = (
  configs?: AudienceConfigApi[],
): AudienceConfigApi | undefined => {
  if (_.isEmpty(configs)) {
    return undefined;
  }
  const active = _.find(
    configs,
    (config) => _.get(config, 'meta.status') === ContentConfigStatus.Active,
  );
  return active || _.head(configs);
};

// resolveAudienceSegment maps an Audience config onto what the campaign view
// needs to render a CDP link. A segment audience's `source` is the SegP
// combined value `<namespace>.<segmentName>`, as written by SegPWidget through
// getCombinedSegmentValue; a bare name falls back to the `shared` namespace,
// matching getSegmentNamespace. A table audience has no CDP equivalent and
// reports isSegment: false so callers render no link.
export const resolveAudienceSegment = (
  config?: AudienceConfigApi,
): AudienceSegmentDisplay | null => {
  const body = _.get(config, 'body');
  if (!body) {
    return null;
  }

  const source = _.toString(_.get(body, 'source', '')).trim();
  const audienceName = _.toString(_.get(body, 'name', ''));
  const isSegment = _.get(body, 'type') === AudienceSourceType.Segment;

  if (!isSegment || !source) {
    return {
      audienceName,
      isSegment: isSegment && Boolean(source),
      namespace: '',
      segmentName: '',
      source,
    };
  }

  return {
    audienceName,
    isSegment: true,
    ...splitSegmentSource(source),
    source,
  };
};
